import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

//UTILS
import { setFavoriteAccount } from "../../utils/favoriteAccount";
import {
  updateAccounts,
  updateAccountSelected,
} from "../../store/slices/userAccountsSlice";
import { useAppSelector } from "../../store/hooks";

//UI COMPONENTS
import BottomSheet from "../BottomSheet/BottomSheet";
import Input from "../Input/Input";

//STYLES
import styles from "./AccountSelectorMobile.module.scss";

//TYPES
import { UserAccount } from "../../types/types";

//ASSETS
import EmptyStar from "../../assets/images/icons/empty-star-icon.svg?react";
import YellowStar from "../../assets/images/icons/filled-star-icon.svg?react";
import SearchIcon from "../../assets/images/icons/search-icon.svg?react";

interface Props {
  isOpen: boolean;
  handleClose: () => void;
}

const AccountsSelectorMobile = (props: Props) => {
  const {
    sheetContent,
    titleStyle,
    optionsStyle,
    optionButton,
    rowTitle,
    accountSelectedStyles,
    mirroredContainer,
    starButton,
    noResults,
  } = styles;
  const { isOpen, handleClose } = props;

  const [searchValue, setSearchValue] = useState<string>("");
  const [localAccounts, setLocalAccounts] = useState<UserAccount[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const dispatch = useDispatch();
  const { accounts, accountSelected } = useAppSelector(
    (state) => state.userAccounts
  );
  const { user } = useAppSelector((state) => state.authentication);

  const handleChangeAccount = (accountNumber: string) => {
    // Use localAccounts if there are pending changes
    const accountsToUse = hasPendingChanges ? localAccounts : accounts;
    const account = accountsToUse.find((acc) => acc.number == accountNumber);

    if (account) {
      dispatch(updateAccountSelected(account));
      handleClose();
    }
  };

  const handleToggleFavorite = (e: React.MouseEvent, accountNumber: string) => {
    e.stopPropagation();

    // Update ONLY local state (not Redux yet)
    const accountsWithFavorite = localAccounts.map((account) => {
      if (account.number === accountNumber) {
        return { ...account, isFavorite: !account.isFavorite };
      }
      return { ...account, isFavorite: false };
    });

    // Save to localStorage immediately
    setFavoriteAccount({
      accountNumber:
        accountsWithFavorite.find((acc) => acc.isFavorite)?.number || null,
      email: user?.email ?? "",
    });

    // Update local state
    setLocalAccounts(accountsWithFavorite);
    setHasPendingChanges(true);
  };

  // Initialize local accounts when bottom sheet opens
  useEffect(() => {
    if (isOpen) {
      setLocalAccounts([...accounts]);
      setHasPendingChanges(false);
    }
  }, [isOpen, accounts]);

  // On bottom sheet close, apply changes and reorder
  useEffect(() => {
    if (!isOpen && hasPendingChanges) {
      // 1. Sort: favorite first, rest alphabetically
      const sortedAccounts = [...localAccounts].sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
      });

      // 2. Update Redux
      dispatch(updateAccounts(sortedAccounts));

      // 3. Clear local state
      setHasPendingChanges(false);
      setLocalAccounts([]);
    }
  }, [isOpen, hasPendingChanges, localAccounts, dispatch]);

  // Use localAccounts if there are pending changes, otherwise use Redux accounts
  const getAccountsToDisplay = () => {
    return hasPendingChanges ? localAccounts : accounts;
  };

  const filteredAccounts = () => {
    const accountsToFilter = getAccountsToDisplay();

    if (searchValue.length > 0) {
      const value = searchValue.toLowerCase();
      return accountsToFilter.filter(
        (account) =>
          account.number.toLowerCase().includes(value) ||
          account.name.toLowerCase().includes(value)
      );
    }

    return accountsToFilter;
  };

  // Calculate dynamic height based on number of items
  const calculateListHeight = useMemo(() => {
    const itemCount = filteredAccounts().length;

    // rowTitle height (2.5rem = 40px) + padding top (0.5rem = 8px) + padding bottom (0.5rem = 8px) = 56px
    const ITEM_HEIGHT = 56;

    const MIN_HEIGHT = 112;
    const MAX_HEIGHT_VH = 60;
    const MAX_HEIGHT_PX = (window.innerHeight * MAX_HEIGHT_VH) / 100;

    const calculatedHeight = itemCount * ITEM_HEIGHT;
    const finalHeight = Math.min(
      Math.max(calculatedHeight, MIN_HEIGHT),
      MAX_HEIGHT_PX
    );

    return finalHeight;
  }, [filteredAccounts().length]);

  // Render function for each item in virtualized list
  const ItemContent = (_: number, account: UserAccount) => {
    return (
      <button
        key={account.number}
        className={optionButton}
        onClick={() => {
          handleChangeAccount(account.number);
          handleClose();
        }}
      >
        <div className={rowTitle}>
          <p
            className={
              accountSelected?.number == account.number
                ? accountSelectedStyles
                : ""
            }
          >
            {account.name}
            <span>#{account.number}</span>{" "}
          </p>
          {account.unifyState == "MIRRORED" && (
            <span className={mirroredContainer}>Unificada</span>
          )}
          <div
            className={starButton}
            onClick={(e) => handleToggleFavorite(e, account.number)}
          >
            {account.isFavorite ? <YellowStar /> : <EmptyStar />}
          </div>
        </div>
      </button>
    );
  };

  const filtered = filteredAccounts();

  return (
    <BottomSheet open={isOpen} onClose={handleClose}>
      <div className={sheetContent}>
        <h1 className={titleStyle}>
          {accounts.length === 1 ? "Tu cuenta" : "Elegir Cuenta"}
        </h1>

        {/** input search */}
        {accounts.length > 3 && (
          <Input
            defaultValue={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            startContent={<SearchIcon />}
            className={`pb-6`}
            placeholder="Buscar comitentes"
          />
        )}

        <div className={optionsStyle}>
          {filtered.length > 0 ? (
            <div
              style={{
                width: "100%",
                height: `${calculateListHeight}px`,
              }}
            >
              <Virtuoso
                ref={virtuosoRef}
                style={{
                  height: "100%",
                  width: "100%",
                }}
                data={filtered}
                itemContent={ItemContent}
              />
            </div>
          ) : (
            <div className={noResults}>
              <p>No se encontraron cuentas</p>
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default AccountsSelectorMobile;
